import React from 'react';
import {cloneDeep} from 'lodash';
import Util from '../../../util/util';
import DropdownV2 from '../../dropdowns/dropdown';
import AssignedTableTile from '../../table-tiles/assigned_table_tile';
import moment from 'moment';
import {hasTaskStatusChangePermission} from '../../../util/PermissionsUtil';

/**
 * PIKA-790. The My Work row's status dropdown is a status affordance like the board and the task
 * tables, and it commits the same UpdateTask mutation via Util.changeTaskStatus below — so
 * without this gate the server's TASK_STATUS_CHANGE guard rejects the pick and the user is left
 * with an error toast, the UI/server mismatch the flag-parity rule exists to prevent.
 *
 * The permission is read here rather than in Task_row.js deliberately. The caller's `disabled`
 * also gates the time-registration and progress popups, which this permission does not govern, so
 * the two must not be folded together — and keeping the decision in this plain-function module
 * makes it unit-testable, which the Relay fragment container the caller lives in is not.
 */
export const statusElement = (task, intl, disabled) => {
	const statusDisabled = disabled || !hasTaskStatusChangePermission();
	return (
		<DropdownV2
			focusOnClick={true}
			optionClickEvent={true}
			onChange={status => Util.changeTaskStatus(task, status, intl)}
			options={cloneDeep(task.project.statusColumnsV2.edges)
				.sort((a, b) => {
					const comparisonResult = a.node.order - b.node.order;
					if (comparisonResult > 0) {
						return 1;
					} else if (comparisonResult < 0) {
						return -1;
					} else {
						return 0;
					}
				})
				.map(statusColumn => {
					return {
						label: statusColumn.node.name,
						value: statusColumn.node.id,
						logo: statusColumn.node.jiraStatusId ? 'jira-logo' : undefined,
					};
				})}
			value={task.statusColumnV2.id}
			hideLabel={true}
			restrictWidth={true}
			customHeight={30}
			customWidth={166}
			inputCy={'status-list'}
			listDataCy={'status'}
			disabled={statusDisabled}
		/>
	);
};

export const assigneesElement = (task, assignedPersons) => {
	return (
		<AssignedTableTile
			disabled={true}
			assignablePersons={task.project && task.project.projectPersons ? task.project.projectPersons.edges : []}
			assignedPersons={assignedPersons || task.assignedPersons}
			useTableStyling={true}
			isMultiSelect={true}
			id={task.id}
			key={'assigned-person'}
			task={task}
			maxDisplayed={4}
			cy={'assigned-person'}
			suggestedPersonRoleFilter={task.role}
			isClientUser={false}
		/>
	);
};

export const ownerElement = (task, disabled = true, assignablePersons) => {
	return (
		<AssignedTableTile
			disabled={disabled}
			assignablePersons={
				assignablePersons
					? assignablePersons
					: task.project && task.project.projectPersons
					? task.project.projectPersons.edges
					: []
			}
			assignedPersons={task.owner ? [task.owner] : []}
			useTableStyling={true}
			isMultiSelect={false}
			cy="task-modal-owner-dropdown"
			task={task}
			useSmallerStyling={true}
			withAddIcon={false}
			closeOnChoice={true}
			wideExpanded={true}
			isClientUser={false}
			isOwner={true}
		/>
	);
};

export const getDateText = (day, month, year, intl) => {
	const {formatMessage} = intl;
	const today = moment();
	if (day == null) {
		return '';
	}
	const date = Util.CreateNonUtcMomentDate(year, month, day);
	if (today.isSame(date, 'date')) {
		return <span className={'today date-text'}>{formatMessage({id: 'common.today'})}</span>;
	} else if (today.add(1, 'days').isSame(date, 'date')) {
		return <span className={'today date-text'}>{formatMessage({id: 'common.tomorrow'})}</span>;
	} else if (today.isAfter(date, 'date')) {
		return <span className={'overdue date-text'}>{date.format('DD MMM')}</span>;
	}
	return <span className={'date-text'}>{date.format('DD MMM')}</span>;
};

export const getEstimateHours = (task, intl) => {
	return Util.convertMinutesToFullHour(task.estimateForecast, intl);
};

export const getEstimatePoints = (task, intl) => {
	return task.estimateForecast + intl.formatMessage({id: 'common.points.short'});
};

export const getRemainingHours = (task, intl) => {
	return Util.convertMinutesToFullHour(task.timeLeft, intl);
};

export const getRemainingPoints = (task, intl) => {
	return task.timeLeft + intl.formatMessage({id: 'common.points.short'});
};

export const getProgressPercentage = (task, intl) => {
	return task.progress + '%';
};
